import useTranslation from 'next-translate/useTranslation';

// Third-party
import clsx from 'clsx';
import { includes } from 'lodash';

// Hooks
import { useMessageFilter } from '@/utils/hooks/conversations/useMessageFilter';
import useActiveSocialProfile from '@/utils/hooks/user/useActiveSocialProfile';

import type { SocialInboxType } from '@/components/pages/conversations/SocialInbox/types';

import { INSTAGRAM } from '@/lib/socialProfile';
// Types/Libs
import {
    DEFAULT_SOCIAL_INBOX_TYPES,
    INSTAGRAM_ONLY_FEATURES,
    MESSAGE_FILTER_META,
    SOCIAL_INBOX_MANAGING_OPTIONS
} from '@/components/pages/conversations/SocialInbox/const';

// Components
import Badge from '@/components/base/Badge';
import Dropdown, { BaseItem } from '@/components/base/Dropdown';
import Icon from '@/components/base/Icon';
import Tooltip from '@/components/base/Tooltip';
import Shining from '@/components/effects/Shining';
import { SocialInboxTypeTitle } from '@/components/pages/conversations/SocialInbox';

export const SocialInboxMenuMobile = () => {
    const { t } = useTranslation('conversations');
    const { activeSocialProfile } = useActiveSocialProfile();

    const { messageFilter, navigateToFilter } = useMessageFilter();

    const isInstagram = activeSocialProfile?.social_network_type === INSTAGRAM;

    const isMenuOptionDisabled = (socialInboxType: SocialInboxType) => {
        const isInstagramOnlyFeature = includes(INSTAGRAM_ONLY_FEATURES, socialInboxType);
        return (
            (isInstagramOnlyFeature && !isInstagram) ||
            socialInboxType === MESSAGE_FILTER_META.dm.key
        );
    };

    const renderDropDownOption = (filter: SocialInboxType) => {
        return (
            <Dropdown.Item
                key={filter}
                onClick={() => navigateToFilter(filter)}
                disabled={isMenuOptionDisabled(filter)}>
                {({ active, disabled }) => (
                    <Tooltip
                        content={t('social_inbox.not_yet_available')}
                        interactive={true}
                        appendTo={() => document.body}
                        placement="right"
                        disabled={!disabled}>
                        <span className="z-20 flex items-center">
                            <BaseItem
                                active={active}
                                disabled={disabled}
                                className={clsx('flex items-center gap-2', {
                                    'cursor-not-allowed opacity-50': disabled
                                })}
                                key={filter}>
                                <SocialInboxTypeTitle messageFilter={filter} />

                                {filter === MESSAGE_FILTER_META.dm.key && (
                                    <Tooltip
                                        content={t('social_inbox.coming_soon_dm')}
                                        interactive={true}
                                        appendTo={() => document.body}
                                        placement="right">
                                        <span className="z-22 flex items-center">
                                            <Shining className="rounded-full">
                                                <Badge variant="coming-soon" />
                                            </Shining>
                                        </span>
                                    </Tooltip>
                                )}
                            </BaseItem>
                        </span>
                    </Tooltip>
                )}
            </Dropdown.Item>
        );
    };

    return (
        <div
            data-testid="social-inbox-social-inbox-mobile-menu"
            className="w-full space-y-3 bg-white p-5 dark:bg-icoDarkMode-deepBlack">
            {/* TODO: Uncomment when Direct Messages are available */}
            {/* <SocialInboxTitle /> */}

            <Dropdown
                hasBorder={true}
                classNameWrapper="w-full"
                className="!z-15"
                size="full"
                button={
                    <Dropdown.Button className="flex w-full items-center justify-between bg-icoBlue-100 px-5 py-4 text-icoBlue dark:bg-icoDarkMode-beton">
                        <SocialInboxTypeTitle
                            messageFilter={messageFilter}
                            selected
                            classNameTextSize="text-16"
                        />
                        <Icon icon="chevron-bottom " className="text-16" />
                    </Dropdown.Button>
                }>
                <Dropdown.Divider>
                    {[...DEFAULT_SOCIAL_INBOX_TYPES, ...SOCIAL_INBOX_MANAGING_OPTIONS]
                        .filter((e) => e !== messageFilter)
                        .map(renderDropDownOption)}
                </Dropdown.Divider>
            </Dropdown>
        </div>
    );
};
